import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUSES = ['nová', 'potvrzená', 'připravená', 'doručená', 'zrušená'];
const STATUS_STYLES = {
  'nová':        { bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
  'potvrzená':   { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  'připravená':  { bg: 'bg-indigo-100', text: 'text-indigo-800', dot: 'bg-indigo-500' },
  'doručená':    { bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  'zrušená':     { bg: 'bg-rose-100', text: 'text-rose-800', dot: 'bg-rose-500' },
};

const fmt = (n) => (n || 0).toLocaleString('cs-CZ') + ' Kč';
const fmtDate = (iso) => new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const BeraniOrdersAdminPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API}/berani/orders?limit=200`);
      const d = await r.json();
      setOrders(d.orders || []);
    } catch (e) {
      setError('Chyba při načítání objednávek');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const updateStatus = async (orderId, newStatus) => {
    try {
      const r = await fetch(`${API}/berani/orders/${orderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!r.ok) throw new Error('Update failed');
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? { ...o, status: newStatus } : o)));
    } catch (e) {
      alert('Nepodařilo se změnit stav objednávky');
    }
  };

  const matchesSearch = (o) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(q) ||
      o.company_name?.toLowerCase().includes(q) ||
      o.ico?.toLowerCase().includes(q) ||
      o.opponent?.toLowerCase().includes(q) ||
      o.skybox_number?.toLowerCase().includes(q) ||
      o.email?.toLowerCase().includes(q)
    );
  };

  const matchesDateRange = (o) => {
    if (!dateFrom && !dateTo) return true;
    const md = o.match_date;
    if (dateFrom && md < dateFrom) return false;
    if (dateTo && md > dateTo) return false;
    return true;
  };

  const filtered = orders
    .filter((o) => (statusFilter === 'all' ? true : o.status === statusFilter))
    .filter(matchesSearch)
    .filter(matchesDateRange);

  const stats = STATUSES.reduce((s, st) => ({ ...s, [st]: orders.filter((o) => o.status === st).length }), {});
  const totalRevenue = filtered.filter((o) => o.status !== 'zrušená').reduce((s, o) => s + (o.total_price || 0), 0);

  const exportCsv = () => {
    const headers = [
      'Číslo objednávky', 'Vytvořeno', 'Stav', 'Firma', 'IČO', 'E-mail', 'Telefon',
      'Datum utkání', 'Čas', 'Soupeř', 'SKYBOX', 'Počet osob', 'Balíčky', 'Celkem (Kč)', 'Poznámka',
    ];
    const rows = filtered.map((o) => [
      o.order_number,
      fmtDate(o.created_at),
      o.status,
      o.company_name,
      o.ico,
      o.email,
      o.phone,
      o.match_date,
      o.match_time || '',
      o.opponent,
      o.skybox_number,
      o.person_count,
      (o.items || []).map((it) => `${it.quantity}× ${it.package_code} ${it.package_name}`).join(' | '),
      o.total_price,
      (o.note || '').replace(/[\r\n]+/g, ' '),
    ]);
    const escapeCell = (v) => {
      const s = String(v ?? '');
      return /[";,\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers, ...rows].map((r) => r.map(escapeCell).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `Berani_Skybox_Objednavky_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
  };
  const hasActiveFilter = search || dateFrom || dateTo || statusFilter !== 'all';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="berani-orders-admin">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-slate-500 font-semibold">SPRÁVA · BERANI VIP SKYBOX CATERING</div>
            <h1 className="text-2xl font-black text-slate-900 mt-1">Objednávky</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/admin" className="text-sm text-slate-600 hover:text-slate-900">← Zpět na správu nabídek</a>
            <button onClick={fetchOrders} data-testid="refresh-btn" className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold">
              Obnovit
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          {STATUSES.map((s) => {
            const style = STATUS_STYLES[s];
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
                data-testid={`stat-${s}`}
                className={`p-4 rounded-xl text-left border transition-all ${statusFilter === s ? 'border-slate-900 shadow-lg' : 'border-slate-200 hover:border-slate-400'} bg-white`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${style.dot}`} />
                  <div className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase">{s}</div>
                </div>
                <div className="text-2xl font-black text-slate-900 mt-1">{stats[s]}</div>
              </button>
            );
          })}
        </div>

        <div className="mb-4 rounded-xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
            <div>
              <label className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase mb-1 block">Vyhledávání</label>
              <input
                data-testid="admin-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Číslo objednávky · soupeř · firma · IČO · SKYBOX · e-mail…"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 focus:outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase mb-1 block">Zápas od</label>
              <input
                data-testid="admin-date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-slate-300 focus:border-slate-900 focus:outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase mb-1 block">Zápas do</label>
              <input
                data-testid="admin-date-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2.5 rounded-lg border border-slate-300 focus:border-slate-900 focus:outline-none text-sm"
              />
            </div>
            <div className="flex gap-2">
              {hasActiveFilter && (
                <button
                  data-testid="clear-filters-btn"
                  onClick={clearFilters}
                  className="px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"
                >
                  ✕ Zrušit filtry
                </button>
              )}
              <button
                data-testid="export-csv-btn"
                onClick={exportCsv}
                disabled={filtered.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold shadow-sm"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 4v12m0 0l-5-5m5 5l5-5M5 21h14" />
                </svg>
                Stáhnout přehled ({filtered.length})
              </button>
            </div>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${statusFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}
            >
              Vše ({orders.length})
            </button>
            <div className="text-xs text-slate-500">
              Zobrazeno {filtered.length} z {orders.length}
            </div>
          </div>
          <div className="text-sm text-slate-600">
            Součet obratu (zobrazené, bez zrušených): <strong className="text-slate-900">{fmt(totalRevenue)}</strong>
          </div>
        </div>

        {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>}

        {loading ? (
          <div className="text-center py-16 text-slate-500">Načítám objednávky…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 rounded-xl bg-white border border-dashed border-slate-300">
            <div className="text-slate-500">Žádné objednávky {statusFilter !== 'all' ? `ve stavu „${statusFilter}"` : ''}.</div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-[11px] tracking-widest">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">ČÍSLO</th>
                    <th className="px-4 py-3 text-left font-semibold">VYTVOŘENO</th>
                    <th className="px-4 py-3 text-left font-semibold">OBJEDNAVATEL</th>
                    <th className="px-4 py-3 text-left font-semibold">ZÁPAS</th>
                    <th className="px-4 py-3 text-left font-semibold">SKYBOX</th>
                    <th className="px-4 py-3 text-right font-semibold">CENA</th>
                    <th className="px-4 py-3 text-left font-semibold">STAV</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((o) => {
                    const style = STATUS_STYLES[o.status] || STATUS_STYLES['nová'];
                    const isOpen = expanded === o.order_id;
                    return (
                      <React.Fragment key={o.order_id}>
                        <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`order-row-${o.order_number}`}>
                          <td className="px-4 py-3 font-mono text-xs text-slate-700">{o.order_number}</td>
                          <td className="px-4 py-3 text-slate-600">{fmtDate(o.created_at)}</td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{o.company_name}</div>
                            <div className="text-xs text-slate-500">IČO {o.ico}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-slate-900">— {o.opponent}</div>
                            <div className="text-xs text-slate-500">{o.match_date}{o.match_time ? ' · ' + o.match_time : ''}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-900">{o.skybox_number}</div>
                            <div className="text-xs text-slate-500">{o.person_count} os.</div>
                          </td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-slate-900">{fmt(o.total_price)}</td>
                          <td className="px-4 py-3">
                            <select
                              data-testid={`status-select-${o.order_number}`}
                              value={o.status}
                              onChange={(e) => updateStatus(o.order_id, e.target.value)}
                              className={`text-xs font-semibold rounded-lg px-2.5 py-1.5 border-0 focus:ring-2 focus:ring-slate-900 cursor-pointer ${style.bg} ${style.text}`}
                            >
                              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              data-testid={`toggle-${o.order_number}`}
                              onClick={() => setExpanded(isOpen ? null : o.order_id)}
                              className="text-slate-500 hover:text-slate-900 text-sm font-semibold"
                            >
                              {isOpen ? '− Skrýt' : '+ Detail'}
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50 border-t border-slate-100">
                            <td colSpan={8} className="px-6 py-5">
                              <div className="grid md:grid-cols-3 gap-6">
                                <div>
                                  <div className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase mb-2">Kontakt</div>
                                  <div className="text-sm text-slate-800">{o.email}</div>
                                  <div className="text-sm text-slate-800">{o.phone}</div>
                                </div>
                                <div className="md:col-span-2">
                                  <div className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase mb-2">Balíčky</div>
                                  <div className="space-y-1">
                                    {(o.items || []).map((it, i) => (
                                      <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-200 last:border-0">
                                        <span className="text-slate-700">
                                          <span className="font-mono text-slate-500 mr-2">{it.package_code}</span>
                                          {it.package_name}
                                        </span>
                                        <span className="text-slate-900 font-mono">
                                          {it.quantity}× · {fmt(it.quantity * it.unit_price)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              {o.note && (
                                <div className="mt-4 pt-4 border-t border-slate-200">
                                  <div className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase mb-1">Poznámka</div>
                                  <div className="text-sm text-slate-700 italic">{o.note}</div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeraniOrdersAdminPage;
